import type { PaletteColorName } from '@/features/colors/color.types';
import { useCategoryOptionColorsResolver } from '@/features/colors/hooks/useCategoryOptionColorsResolver';
import { PALETTE_COLOR_NAMES } from '@/features/colors/palettes';
import {
  Box,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  SimpleGrid,
} from '@/ui';

type Props = {
  currentColorName: PaletteColorName;
  trigger: React.ReactNode;
  onChange: (colorName: PaletteColorName) => void;
};

export const ColorSwatchPopover = ({
  currentColorName,
  trigger,
  onChange,
}: Props) => {
  // Swatches are palette slots displayed through the experience's palette
  // and colorMode.
  const resolveOptionColors = useCategoryOptionColorsResolver();
  const displayColor = (colorName: PaletteColorName) => {
    const { backgroundColor } = resolveOptionColors(colorName);
    return backgroundColor;
  };

  return (
    <Popover closeOnBlur placement="bottom-start">
      {({ onClose }) => (
        <>
          <PopoverTrigger>{trigger}</PopoverTrigger>
          <PopoverContent
            border="1px solid"
            borderColor="line.DEFAULT"
            borderRadius="ah-xl"
            boxShadow="0 12px 32px -10px rgba(0,0,0,0.26)"
            w="auto"
          >
            <PopoverBody p={2}>
              <SimpleGrid columns={4} gap={1.5}>
                {PALETTE_COLOR_NAMES.map((colorName) => (
                  <Box
                    _hover={{ transform: 'scale(1.1)' }}
                    aria-label={`Select color ${colorName}`}
                    aria-pressed={currentColorName === colorName}
                    as="button"
                    bg={displayColor(colorName)}
                    border="2px solid"
                    borderRadius="ah-md"
                    cursor="pointer"
                    h="26px"
                    key={colorName}
                    transition="transform 0.1s"
                    type="button"
                    w="26px"
                    borderColor={
                      currentColorName === colorName
                        ? 'ink.strong'
                        : 'transparent'
                    }
                    onClick={() => {
                      onChange(colorName);
                      onClose();
                    }}
                  />
                ))}
              </SimpleGrid>
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
};
